import { Link } from 'react-router-dom';

import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'link';
type Size = 'md' | 'lg';

const base =
  'inline-flex items-center justify-center gap-2 rounded-md font-semibold tracking-tight transition-colors duration-200 ease-out-expo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-yellow focus-visible:ring-offset-2';

const sizes: Record<Size, string> = {
  md: 'h-11 px-5 text-sm',
  lg: 'h-14 px-7 text-base',
};

const variants: Record<Variant, string> = {
  primary: 'bg-brand-yellow text-brand-charcoal hover:bg-brand-yellow-deep',
  secondary:
    'border border-brand-charcoal text-brand-charcoal hover:bg-brand-charcoal hover:text-brand-white',
  link: 'h-auto px-0 text-brand-charcoal hover:text-brand-yellow-deep',
};

interface CommonProps {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
  className?: string;
}

type LinkButtonProps = CommonProps & {
  to: string;
  href?: never;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;

type ExternalButtonProps = CommonProps & {
  href: string;
  to?: never;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;

type ActionButtonProps = CommonProps & {
  to?: never;
  href?: never;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export type ButtonProps = LinkButtonProps | ExternalButtonProps | ActionButtonProps;

export function Button(props: ButtonProps): JSX.Element {
  const { variant = 'primary', size = 'md', className = '', children } = props;
  const classes =
    `${base} ${variants[variant]} ${variant !== 'link' ? sizes[size] : ''} ${className}`.trim();

  if ('to' in props && props.to) {
    const { to, variant: _v, size: _s, className: _c, children: _ch, ...rest } = props;
    return (
      <Link to={to} className={classes} {...rest}>
        {children}
      </Link>
    );
  }

  if ('href' in props && props.href) {
    const { href, variant: _v, size: _s, className: _c, children: _ch, ...rest } = props;
    return (
      <a href={href} className={classes} {...rest}>
        {children}
      </a>
    );
  }

  const {
    variant: _v,
    size: _s,
    className: _c,
    children: _ch,
    ...rest
  } = props as ActionButtonProps;
  return (
    <button className={classes} {...rest}>
      {children}
    </button>
  );
}
